import {
  ErrorAttribute,
  FreeKickAttribute,
  InterceptionAttribute,
  MissedPenaltyAttribute,
  PenaltyAttribute,
  PenaltySavedByGoalkeeperAttribute,
  PlayShortHandedAttribute,
  RedCardAttribute,
  ShotBlockedAttribute,
  ShotOffTargetAttribute,
  ShotOnTargetAttribute,
  SoccerEventStatusId,
  SoccerFoulAttribute,
  SoccerGoalAttribute,
  SoccerIncidentId,
  SoccerSubParticipantStatId,
  VideoAssistantRefereeAttribute,
  YellowCardAttribute,
} from '@collector/sportsapi-client-legacy'
import { IncidentPlayerOption } from '@desktop/views/Relation/types'

import { HistoryIncidentsConfig } from '../../configuration'

function isSubParticipantOnBench(option: IncidentPlayerOption): boolean {
  return (
    option.metadata.stats[SoccerSubParticipantStatId.OnBench] === 1 &&
    option.metadata.stats[SoccerSubParticipantStatId.Appearance] === 0
  )
}

export const historyIncidentsConfig: HistoryIncidentsConfig<SoccerIncidentId> =
  {
    [SoccerIncidentId.SubstitutionIn]: {
      participant: {
        name: 'in',
        required: true,
        keepSelectedOption: true,
        filterSubParticipantsOptions: (options) => {
          return options.filter(isSubParticipantOnBench)
        },
      },
    },
    [SoccerIncidentId.SubstitutionOut]: {
      participant: {
        name: 'out',
        required: true,
        keepSelectedOption: true,
        filterSubParticipantsOptions: (options) => {
          return options.filter((option) => !isSubParticipantOnBench(option))
        },
      },
    },
    [SoccerIncidentId.Goal]: {
      participant: {
        name: 'scorer',
        shouldLoadOppositeSubParticipants: (attributeIds) =>
          attributeIds.includes(SoccerGoalAttribute.OwnGoal),
        required: true,
      },
      assistant: (incident) => {
        switch (incident.status_id) {
          case SoccerEventStatusId.PenaltyShootout:
            return undefined
          default:
            return {
              name: 'assistant',
              shouldLoadOppositeSubParticipants: (attributeIds) =>
                attributeIds.includes(SoccerGoalAttribute.OwnGoal),
              isAvailable: ({ attribute_ids }) =>
                !attribute_ids?.includes(SoccerGoalAttribute.NoAssist),
            }
        }
      },
      attributeIds: (incident) => {
        switch (incident.status_id) {
          case SoccerEventStatusId.PenaltyShootout:
            return undefined
          default:
            return [
              SoccerGoalAttribute.NoAssist,
              SoccerGoalAttribute.PenaltyGoal,
              SoccerGoalAttribute.OwnGoal,
              SoccerGoalAttribute.RightFoot,
              SoccerGoalAttribute.LeftFoot,
              SoccerGoalAttribute.Head,
              SoccerGoalAttribute.OtherBodyPart,
              SoccerGoalAttribute.DirectFreeKick,
              SoccerGoalAttribute.Crossbar,
              SoccerGoalAttribute.Post,
              SoccerGoalAttribute.OpenPlay,
              SoccerGoalAttribute.FastBreak,
              SoccerGoalAttribute.SetPiece,
              SoccerGoalAttribute.InsidePenaltyArea,
              SoccerGoalAttribute.OutsidePenaltyArea,
              SoccerGoalAttribute.UpperLeft,
              SoccerGoalAttribute.UpperMiddle,
              SoccerGoalAttribute.UpperRight,
              SoccerGoalAttribute.BottomLeft,
              SoccerGoalAttribute.BottomMiddle,
              SoccerGoalAttribute.BottomRight,
              SoccerGoalAttribute.Normal,
              SoccerGoalAttribute.FirstTouch,
              SoccerGoalAttribute.Volley,
              SoccerGoalAttribute.BicycleKick,
              SoccerGoalAttribute.PowerShot,
              SoccerGoalAttribute.Chip,
              SoccerGoalAttribute.NoPressure,
              SoccerGoalAttribute.LightPressure,
              SoccerGoalAttribute.HighPressure,
              SoccerGoalAttribute.ClearPosition,
              SoccerGoalAttribute.OneDefender,
              SoccerGoalAttribute.TwoOrMoreDefenders,
              SoccerGoalAttribute.BigChanceSuccessful,
              SoccerGoalAttribute.OneOnOne,
              SoccerGoalAttribute.UncomfortablePosition,
              SoccerGoalAttribute.EmptyNet,
              SoccerGoalAttribute.GoalkeeperInvolvement,
            ]
        }
      },
    },
    [SoccerIncidentId.Penalty]: {
      participant: {
        name: 'penalty taker',
        required: true,
      },
      assistant: 'penalty winner',
      attributeIds: [PenaltyAttribute.Retaken],
    },
    [SoccerIncidentId.MissedPenalty]: {
      participant: {
        name: 'executor',
        required: true,
      },
      attributeIds: [
        MissedPenaltyAttribute.RightFoot,
        MissedPenaltyAttribute.LeftFoot,
        MissedPenaltyAttribute.ShotWoodwork,
        MissedPenaltyAttribute.BigChanceMissed,
      ],
      swappable: (incident) =>
        incident.status_id !== SoccerEventStatusId.PenaltyShootout,
    },
    [SoccerIncidentId.PenaltySavedByGoalkeeper]: {
      participant: {
        name: 'executor',
        required: true,
      },
      attributeIds: [
        PenaltySavedByGoalkeeperAttribute.RightFoot,
        PenaltySavedByGoalkeeperAttribute.LeftFoot,
        PenaltySavedByGoalkeeperAttribute.UpperLeft,
        PenaltySavedByGoalkeeperAttribute.UpperMiddle,
        PenaltySavedByGoalkeeperAttribute.UpperRight,
        PenaltySavedByGoalkeeperAttribute.BottomLeft,
        PenaltySavedByGoalkeeperAttribute.BottomMiddle,
        PenaltySavedByGoalkeeperAttribute.BottomRight,
        PenaltySavedByGoalkeeperAttribute.BigChanceMissed,
      ],
    },
    [SoccerIncidentId.RedCard]: {
      participant: {
        name: 'given',
        required: true,
      },
      attributeIds: [
        RedCardAttribute.SecondYellowCard,
        RedCardAttribute.OffField,
        RedCardAttribute.Handball,
        RedCardAttribute.TacticalFoul,
        RedCardAttribute.SeriousFoul,
        RedCardAttribute.DangerousPlay,
        RedCardAttribute.RefereeAbuseArgument,
        RedCardAttribute.ViolentConduct,
      ],
    },
    [SoccerIncidentId.YellowCard]: {
      participant: {
        name: 'given',
        required: true,
      },
      attributeIds: [
        YellowCardAttribute.OffField,
        YellowCardAttribute.Handball,
        YellowCardAttribute.TacticalFoul,
        YellowCardAttribute.SeriousFoul,
        YellowCardAttribute.DangerousPlay,
        YellowCardAttribute.UnsportingBehaviour,
        YellowCardAttribute.RefereeAbuseArgument,
        YellowCardAttribute.ViolentConduct,
        YellowCardAttribute.Diving,
        YellowCardAttribute.TimeWasting,
        YellowCardAttribute.ExcessiveCelebration,
      ],
    },
    [SoccerIncidentId.ShotOnTarget]: {
      participant: 'taken by',
      assistant: {
        name: 'goal line clearer',
        shouldLoadOppositeSubParticipants: true,
      },
      attributeIds: [
        ShotOnTargetAttribute.GoallineClearance,
        ShotOnTargetAttribute.RightFoot,
        ShotOnTargetAttribute.LeftFoot,
        ShotOnTargetAttribute.Head,
        ShotOnTargetAttribute.OtherBodyPart,
        ShotOnTargetAttribute.InsidePenaltyArea,
        ShotOnTargetAttribute.OutsidePenaltyArea,
        ShotOnTargetAttribute.UpperLeft,
        ShotOnTargetAttribute.UpperMiddle,
        ShotOnTargetAttribute.UpperRight,
        ShotOnTargetAttribute.BottomLeft,
        ShotOnTargetAttribute.BottomMiddle,
        ShotOnTargetAttribute.BottomRight,
        ShotOnTargetAttribute.Normal,
        ShotOnTargetAttribute.FirstTouch,
        ShotOnTargetAttribute.Volley,
        ShotOnTargetAttribute.DirectFreeKick,
        ShotOnTargetAttribute.BicycleKick,
        ShotOnTargetAttribute.PowerShot,
        ShotOnTargetAttribute.Chip,
        ShotOnTargetAttribute.NoPressure,
        ShotOnTargetAttribute.LightPressure,
        ShotOnTargetAttribute.HighPressure,
        ShotOnTargetAttribute.ClearPosition,
        ShotOnTargetAttribute.OneDefender,
        ShotOnTargetAttribute.TwoOrMoreDefenders,
        ShotOnTargetAttribute.BigChanceMissed,
        ShotOnTargetAttribute.OneOnOne,
        ShotOnTargetAttribute.UncomfortablePosition,
        ShotOnTargetAttribute.EmptyNet,
        ShotOnTargetAttribute.EasySave,
        ShotOnTargetAttribute.ImportantSave,
        ShotOnTargetAttribute.SpectacularSave,
      ],
    },
    [SoccerIncidentId.ShotOffTarget]: {
      participant: 'taken by',
      attributeIds: [
        ShotOffTargetAttribute.ShotWoodwork,
        ShotOffTargetAttribute.RightFoot,
        ShotOffTargetAttribute.LeftFoot,
        ShotOffTargetAttribute.Head,
        ShotOffTargetAttribute.OtherBodyPart,
        ShotOffTargetAttribute.Crossbar,
        ShotOffTargetAttribute.Post,
        ShotOffTargetAttribute.InsidePenaltyArea,
        ShotOffTargetAttribute.OutsidePenaltyArea,
        ShotOffTargetAttribute.Normal,
        ShotOffTargetAttribute.FirstTouch,
        ShotOffTargetAttribute.Volley,
        ShotOffTargetAttribute.DirectFreeKick,
        ShotOffTargetAttribute.BicycleKick,
        ShotOffTargetAttribute.Chip,
        ShotOffTargetAttribute.NoPressure,
        ShotOffTargetAttribute.LightPressure,
        ShotOffTargetAttribute.HighPressure,
        ShotOffTargetAttribute.ClearPosition,
        ShotOffTargetAttribute.OneDefender,
        ShotOffTargetAttribute.TwoOrMoreDefenders,
        ShotOffTargetAttribute.BigChanceMissed,
        ShotOffTargetAttribute.OneOnOne,
        ShotOffTargetAttribute.UncomfortablePosition,
        ShotOffTargetAttribute.EmptyNet,
      ],
    },
    [SoccerIncidentId.ShotBlocked]: {
      participant: 'taken by',
      assistant: {
        name: 'blocked by',
        shouldLoadOppositeSubParticipants: true,
      },
      attributeIds: [
        ShotBlockedAttribute.RightFoot,
        ShotBlockedAttribute.LeftFoot,
        ShotBlockedAttribute.Head,
        ShotBlockedAttribute.BlockedByTeammate,
        ShotBlockedAttribute.OtherBodyPart,
        ShotBlockedAttribute.InsidePenaltyArea,
        ShotBlockedAttribute.OutsidePenaltyArea,
        ShotBlockedAttribute.Normal,
        ShotBlockedAttribute.FirstTouch,
        ShotBlockedAttribute.Volley,
        ShotBlockedAttribute.FreeKick,
        ShotBlockedAttribute.BicycleKick,
        ShotBlockedAttribute.Chip,
        ShotBlockedAttribute.NoPressure,
        ShotBlockedAttribute.LightPressure,
        ShotBlockedAttribute.HighPressure,
        ShotBlockedAttribute.ClearPosition,
        ShotBlockedAttribute.OneDefender,
        ShotBlockedAttribute.TwoOrMoreDefenders,
        ShotBlockedAttribute.BigChanceMissed,
        ShotBlockedAttribute.UncomfortablePosition,
        ShotBlockedAttribute.EmptyNet,
      ],
    },
    [SoccerIncidentId.Foul]: {
      participant: 'foul by',
      attributeIds: [SoccerFoulAttribute.CausesPenalty],
    },
    [SoccerIncidentId.FreeKick]: {
      attributeIds: [FreeKickAttribute.DangerousFreeKick],
    },
    [SoccerIncidentId.Offside]: {
      participant: 'caught',
    },
    [SoccerIncidentId.Injury]: { participant: 'player' },
    [SoccerIncidentId.PossibleVAR]: {
      attributeIds: [
        VideoAssistantRefereeAttribute.PossibleGoal,
        VideoAssistantRefereeAttribute.PossiblePenalty,
        VideoAssistantRefereeAttribute.PossibleRedCard,
        VideoAssistantRefereeAttribute.PossibleYellowCard,
      ],
      singleAttributes: true,
    },
    [SoccerIncidentId.AddedTime]: {
      displayInfo: 'minutes',
    },
    [SoccerIncidentId.Tackle]: {
      participant: 'player',
    },
    [SoccerIncidentId.Interception]: {
      participant: 'player',
      attributeIds: [InterceptionAttribute.LastLine],
    },
    [SoccerIncidentId.BallSkills]: {
      participant: 'player',
    },
    [SoccerIncidentId.Error]: {
      participant: 'player',
      attributeIds: [
        ErrorAttribute.LeadingToAttempt,
        ErrorAttribute.LeadingToGoal,
      ],
      singleAttributes: true,
    },
    [SoccerIncidentId.BigChanceCreated]: {
      participant: 'player',
    },
    [SoccerIncidentId.PlayShortHanded]: {
      attributeIds: [
        PlayShortHandedAttribute.Permanent,
        PlayShortHandedAttribute.Temporary,
      ],
      singleAttributes: true,
    },
  }
